import { memo, useEffect, useRef, useState, type ReactNode } from 'react'

import { Feedback } from '@percent/lemonade'
import { AiChatDataPart, UseAiChatResult } from '../../types/aiChat.types'
import { QuestionInput } from '../QuestionInput/QuestionInput'
import { Answer } from '../Answer/Answer'
import { ThinkingIndicator } from './ThinkingIndicator'
import classes from './Conversation.module.scss'

type ConversationTestIds = Partial<{
  page: string
  toolbar: string
  log: string
  userMessage: string
  answer: string
  thinking: string
  error: string
  input: string
  submit: string
}>

export type ConversationProps = Readonly<{
  /** The conversation state from {@link useAiChat}. */
  chat: UseAiChatResult
  /** Render an assistant message's domain data parts (e.g. insights metric cards). */
  renderData?: (data: readonly AiChatDataPart[]) => ReactNode
  /** Host-owned actions shown in a bar above the message log; omitted → no bar. */
  toolbar?: ReactNode
  /** Rotating "thinking" copy shown while awaiting an answer. */
  thinkingMessages: string[]
  /** The single stable line announced while thinking. */
  workingLabel: string
  /** Accessible label for the conversation log region. */
  logAriaLabel: string
  placeholder: string
  submitLabel: string
  /** Optional "Beta" pill copy on the input; omitted → no badge. */
  betaLabel?: string
  inputName?: string
  testIds?: ConversationTestIds
}>

/**
 * The running-conversation shell: a scrolling `role="log"` of user bubbles and
 * assistant {@link Answer}s, a thinking indicator while awaiting a reply, an inline
 * error, and a bottom-pinned follow-up {@link QuestionInput}. Presentational — the
 * host owns the {@link useAiChat} call and page chrome (breadcrumb, title).
 */
export function Conversation({
  chat,
  renderData,
  toolbar,
  thinkingMessages,
  workingLabel,
  logAriaLabel,
  placeholder,
  submitLabel,
  betaLabel,
  inputName,
  testIds,
}: ConversationProps) {
  const scrollRef = useRef<HTMLDivElement>(null)

  const isLoading = chat.status === 'submitted' || chat.status === 'streaming'
  const lastMessage = chat.messages[chat.messages.length - 1]
  // "Answer started" once the assistant message has any content — text OR a data
  // part (the backend emits the metric part before the prose), so the indicator
  // doesn't linger beside an already-rendered result.
  const answerStarted =
    lastMessage?.role === 'assistant' &&
    (lastMessage.text.length > 0 || lastMessage.data.length > 0)
  const awaitingAnswer = isLoading && !answerStarted

  // Keep the latest turn / indicator in view. `scrollTo` is optional because jsdom
  // doesn't implement it — scrolling is a nicety, not a render dependency.
  useEffect(() => {
    scrollRef.current?.scrollTo?.({
      top: scrollRef.current.scrollHeight,
      behavior: 'smooth',
    })
  }, [chat.messages, chat.status])

  return (
    <div className={classes.page} data-testid={testIds?.page}>
      {toolbar && (
        <div className={classes.toolbar} data-testid={testIds?.toolbar}>
          {toolbar}
        </div>
      )}

      {/* Sequential message log (W3C ARIA23): new turns are announced politely as
          they append, without stealing focus. */}
      <div
        className={classes.messages}
        ref={scrollRef}
        role="log"
        aria-label={logAriaLabel}
      >
        {chat.messages.map((message) =>
          message.role === 'user' ? (
            <div key={message.id} className={classes.turn}>
              <div className={classes.userRow}>
                <div
                  className={classes.userBubble}
                  data-testid={testIds?.userMessage}
                >
                  {message.text}
                </div>
              </div>
            </div>
          ) : (
            <div key={message.id} className={classes.turn}>
              <Answer
                message={message}
                renderData={renderData}
                testId={testIds?.answer}
              />
            </div>
          )
        )}

        {awaitingAnswer && (
          <div className={classes.loading}>
            <ThinkingIndicator
              messages={thinkingMessages}
              workingLabel={workingLabel}
              testId={testIds?.thinking}
            />
          </div>
        )}

        {chat.errorMessage && chat.status === 'error' && (
          <div
            className={classes.error}
            role="alert"
            data-testid={testIds?.error}
          >
            <Feedback variant="critical">{chat.errorMessage}</Feedback>
          </div>
        )}
      </div>

      <ConversationInput
        isLoading={isLoading}
        onAsk={chat.ask}
        placeholder={placeholder}
        submitLabel={submitLabel}
        betaLabel={betaLabel}
        inputName={inputName}
        inputTestId={testIds?.input}
        submitTestId={testIds?.submit}
      />
    </div>
  )
}

type ConversationInputProps = Readonly<{
  isLoading: boolean
  onAsk: (question: string) => void
  placeholder: string
  submitLabel: string
  betaLabel?: string
  inputName?: string
  inputTestId?: string
  submitTestId?: string
}>

// Owns the draft text locally, so a keystroke re-renders only the input — not the
// message log above it, which holds the (Recharts) metric cards. Memoised so a turn
// streaming in doesn't re-render the input on every chunk.
const ConversationInput = memo(function ConversationInput({
  isLoading,
  onAsk,
  placeholder,
  submitLabel,
  betaLabel,
  inputName,
  inputTestId,
  submitTestId,
}: ConversationInputProps) {
  const [input, setInput] = useState('')

  const submit = () => {
    const trimmed = input.trim()

    if (!trimmed || isLoading) return

    setInput('')
    onAsk(trimmed)
  }

  return (
    <div className={classes.inputBar}>
      <div className={classes.inputInner}>
        <QuestionInput
          value={input}
          onChange={setInput}
          onSubmit={submit}
          isLoading={isLoading}
          animateBorder={false}
          elevated
          placeholder={placeholder}
          submitLabel={submitLabel}
          betaLabel={betaLabel}
          name={inputName}
          inputTestId={inputTestId}
          submitTestId={submitTestId}
        />
      </div>
    </div>
  )
})
